import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Profile.module.scss';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { refreshUserInfo, useUserService } from '../../../services/user.service';
import { UserInfo } from '../../../types/user.types';
import AccountTabs from '../../Account/AccountTabs';

const Profile = () => {
    const userData = useUserState();
    const userService = useUserService();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);

    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const navigate = useNavigate();
    const userDataRef = useRef(userData);
    userDataRef.current = userData;

    useEffect(() => {
        if (userData.isUserStateLoading) {
            return;
        }

        let active = true;
        const fetchUserInfo = async () => {
            const cachedUserInfo = userDataRef.current.getUserInfo();
            if (!cachedUserInfo?.token) {
                if (active) {
                    setLoading(false);
                }
                return;
            }

            try {
                const refreshedUserInfo = await refreshUserInfo(cachedUserInfo.token);
                if (!active) return;
                userDataRef.current.updateUserInfo(refreshedUserInfo);
                setUserInfo(refreshedUserInfo);
                setUsername(refreshedUserInfo.username);
                setEmail(refreshedUserInfo.email);
                setFirstName(refreshedUserInfo.firstName);
                setLastName(refreshedUserInfo.lastName);
            } catch {
                if (active) {
                    setUserInfo(null);
                    setErrorMessage('Unable to refresh your profile. Reload the page and try again.');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        void fetchUserInfo();
        return () => {
            active = false;
        };
    }, [userData.isUserStateLoading]);

    const handleUpdate = async () => {
        if (!userInfo) return;

        const normalizedUsername = userInfo.canEditUsername === false
            ? userInfo.username
            : username.trim();
        const normalizedEmail = email.trim();
        if (!normalizedUsername) {
            setErrorMessage('Username is required.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
            setErrorMessage('Enter a valid email address.');
            return;
        }

        const updatedUser: UserInfo = {
            userId: userInfo.userId,
            username: normalizedUsername,
            email: normalizedEmail,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            roles: userInfo.roles,
            token: userInfo.token,
            canEditUsername: userInfo.canEditUsername,
            usernameEditRestriction: userInfo.usernameEditRestriction
        };

        setIsSaving(true);
        setErrorMessage(null);
        setMessage(null);
        try {
            const response = await userService.updateUser(updatedUser);
            userData.updateUserInfo(response);
            setUserInfo(response);
            setUsername(response.username);
            setEmail(response.email);
            setFirstName(response.firstName);
            setLastName(response.lastName);
            setIsEditing(false);
            setMessage('Profile updated. Your username can now be used to sign in.');
        } catch (error: any) {
            console.error('Error updating profile:', error);
            setErrorMessage(error?.message || 'Error updating profile. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <span className={styles.heroTag}>Account</span>
                    <h1 className={styles.heroTitle}>Your profile</h1>
                    <p className={styles.heroSubtitle}>
                        Update your details and manage account security in one place.
                    </p>
                </div>
            </section>

            <AccountTabs />

            <section className={styles.section}>
                {loading ? (
                    <div className={styles.content}>
                        <h2 className={styles.cardTitle}>Loading profile</h2>
                        <p className={styles.cardSubtitle}>Fetching your latest account details.</p>
                    </div>
                ) : null}

                {!loading && !userInfo && errorMessage ? (
                    <div className={styles.content}>
                        <div className={styles.errorMessage} role="alert">{errorMessage}</div>
                    </div>
                ) : null}

                {!loading && userInfo ? (
                    <div className={styles.content}>
                        <h2 className={styles.cardTitle}>Profile details</h2>
                        <p className={styles.cardSubtitle}>Keep your information up to date for purchase receipts.</p>

                        {message ? <div className={styles.successMessage} role="status">{message}</div> : null}
                        {errorMessage ? <div className={styles.errorMessage} role="alert">{errorMessage}</div> : null}

                        {isEditing ? (
                                <div className={styles.formGrid}>
                                    <div className={styles.field}>
                                    <label htmlFor="profile-username">Username</label>
                                    <input
                                        id="profile-username"
                                        className={styles.input}
                                        type="text"
                                        value={username}
                                        onChange={(event) => { setUsername(event.target.value); setErrorMessage(null); }}
                                        autoComplete="username"
                                        maxLength={256}
                                        disabled={userInfo.canEditUsername === false}
                                    />
                                    {userInfo.canEditUsername === false ? (
                                        <span className={styles.fieldHelp}>
                                            {userInfo.usernameEditRestriction ?? 'This system account username cannot be changed.'}
                                        </span>
                                    ) : null}
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="profile-email">Email</label>
                                    <input
                                        id="profile-email"
                                        className={styles.input}
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        autoComplete="email"
                                        maxLength={256}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="profile-first-name">First name</label>
                                    <input
                                        id="profile-first-name"
                                        className={styles.input}
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        autoComplete="given-name"
                                        maxLength={256}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="profile-last-name">Last name</label>
                                    <input
                                        id="profile-last-name"
                                        className={styles.input}
                                        type="text"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        autoComplete="family-name"
                                        maxLength={256}
                                    />
                                </div>
                                <div className={styles.actions}>
                                    <button type="button" className={styles.primaryButton} onClick={handleUpdate} disabled={isSaving}>
                                        {isSaving ? 'Updating…' : 'Update profile'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.secondaryButton}
                                        onClick={() => {
                                            setUsername(userInfo?.username ?? '');
                                            setEmail(userInfo?.email ?? '');
                                            setFirstName(userInfo?.firstName ?? '');
                                            setLastName(userInfo?.lastName ?? '');
                                            setErrorMessage(null);
                                            setIsEditing(false);
                                        }}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className={styles.detailGrid}>
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>Username</span>
                                        <span>{username}</span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>Email</span>
                                        <span>{email}</span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>First name</span>
                                        <span>{firstName || 'Not provided'}</span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>Last name</span>
                                        <span>{lastName || 'Not provided'}</span>
                                    </div>
                                </div>
                                <div className={styles.actions}>
                                    <button
                                        type="button"
                                        className={styles.primaryButton}
                                        onClick={() => setIsEditing(true)}
                                    >
                                        Edit profile
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.secondaryButton}
                                        onClick={() => navigate('/change_password')}
                                    >
                                        Change password
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ) : null}
            </section>
        </main>
    );
};

export default Profile;
